'use client';
import React, { useCallback } from 'react';
import { hideAll } from 'tippy.js';

import Layout from '../lib/components/Layout';
import ColumnsDefinition from './ColumnsDef';
import { initTableHint, injectQuotes, sortFDAData } from '../lib/utils';

import { ICalendarProps, IDateRange } from '../lib/interfaces';
import styled from '@benzinga/themetron';
import { Checkbox } from '@benzinga/core-ui';
import { useQueryParams } from '@benzinga/filter-ui';
import { FDA } from '@benzinga/calendar-manager';
import { fetchFDA, FDACalendarTabKeys, FDACalendarTabKeysType } from './Services';
import { DateTime } from 'luxon';

export const fetchFDAData = fetchFDA;
export const FDAColumnsDef = ColumnsDefinition;

const getTabDataByTabIndex = (index: number, dataTabs: [FDA[], FDA[], FDA[], FDA[]]) => {
  return dataTabs[index];
};

export const FDACalendar: React.FC<ICalendarProps> = ({
  calendar,
  calendarData,
  hasStockTableStyling,
  height,
  hiddenColumns,
  initialData,
  initialDateRange,
  initialTab,
  initialTicker,
  ...props
}) => {
  const [queryParams, updateQueryParams] = useQueryParams();
  const [tableReady, setTableReady] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [gridApi, setGridApi] = React.useState<any>(null);
  const [gridColumnApi, setGridColumnApi] = React.useState<any>(null);
  const [activeTabIndex, setActiveTabIndex] = React.useState(getTabIndexByKey(initialTab) || 0);
  const [binaryEventsOnly, setBinaryEventsOnly] = React.useState(queryParams?.binaryOnly ?? false);
  const [historicalData, setHistoricalData] = React.useState<FDA[]>(() => {
    const sortedData = sortFDAData(initialData, binaryEventsOnly);
    return sortedData.historical || [];
  });
  // const [todayData, setTodayData] = React.useState<any>([]);
  const [exactTargetDateData, setExactTargetDateData] = React.useState<FDA[]>(() => {
    const sortedData = sortFDAData(initialData, binaryEventsOnly);
    return sortedData.exact || [];
  });
  const [estimatedDatesData, setEstimatedDatesData] = React.useState<FDA[]>(() => {
    const sortedData = sortFDAData(initialData, binaryEventsOnly);
    return sortedData.estimated || [];
  });

  const [pdufaDateData, setPdufaDateData] = React.useState<FDA[]>(() => {
    const sortedData = sortFDAData(initialData, binaryEventsOnly);
    return sortedData.pdufa || [];
  });

  const dataTabs: [FDA[], FDA[], FDA[], FDA[]] = React.useMemo(
    () => [historicalData, exactTargetDateData, estimatedDatesData, pdufaDateData],
    [historicalData, exactTargetDateData, estimatedDatesData, pdufaDateData],
  );
  const [rowData, setRowData] = React.useState(() => {
    const tabData = getTabDataByTabIndex(activeTabIndex, dataTabs);
    return tabData || [];
  });
  const [dataUpdating, setDataUpdating] = React.useState(false);
  const [currentFilteredRowsCount, setCurrentFilteredRowsCount] = React.useState(null);

  const now = DateTime.local();
  const dateRange: IDateRange = {
    date_from: initialDateRange?.date_from ?? now.startOf('month').toISODate(),
    date_to: initialDateRange?.date_to ?? now.endOf('month').toISODate(),
  };

  const [filters, setFilters] = React.useState({
    'parameters[date_from]': dateRange.date_from,
    'parameters[date_to]': dateRange.date_to,
    'parameters[securities]': queryParams?.securities ?? queryParams?.symbol ?? initialTicker ?? undefined,
  });

  React.useEffect(() => {
    if (calendarData?.serverSide) {
      setTableData(getTabDataByTabIndex(activeTabIndex, dataTabs));
    }
  }, [initialData, calendarData, activeTabIndex, dataTabs]);

  const activeTabKey = FDACalendarTabKeys[activeTabIndex];

  const setFDAData = React.useCallback(
    async (items: FDA[]) => {
      if (!items || items.length === 0) {
        setHistoricalData([]);
        setExactTargetDateData([]);
        setEstimatedDatesData([]);
        setPdufaDateData([]);
        //setTodayData([]);
        return;
      }

      setDataUpdating(true);

      const fdaData = sortFDAData(items, binaryEventsOnly);

      setHistoricalData(fdaData.historical);
      setExactTargetDateData(fdaData.exact);
      setEstimatedDatesData(fdaData.estimated);
      setPdufaDateData(fdaData.pdufa);

      //const todayData = await loadQuotesForData(today);
      fdaData.exact = (await loadQuotesForData(fdaData.exact)) as FDA[];

      //setTodayData(todayData);
      setExactTargetDateData(fdaData.exact);
      setDataUpdating(false);
    },
    [binaryEventsOnly],
  );

  const fetchData = (filterParams: any, tabKey?: FDACalendarTabKeysType) => {
    setIsLoading(true);

    const query = {
      ...filterParams,
      pagesize: 1000,
    };

    fetchFDAData(query, tabKey || FDACalendarTabKeys[activeTabIndex]).then(data => {
      setFDAData(data);
      setIsLoading(false);
    });
  };

  // React.useEffect(() => {
  //   if (tableReady) {
  //     fetchData(filters, activeTabKey);
  //   }
  // }, [activeTabKey, binaryEventsOnly, fetchData, filters, tableReady]);

  React.useEffect(() => {
    if (!dataUpdating) {
      setTableData(getTabDataByTabIndex(activeTabIndex, dataTabs));
    }
  }, [activeTabIndex, dataTabs, dataUpdating]);

  React.useEffect(() => {
    if (gridApi && gridColumnApi) {
      const toggleCatalystColumn = (index: number) => {
        switch (index) {
          case 0:
            gridColumnApi.setColumnVisible('historical_date', true);
            gridColumnApi.setColumnVisible('estimated_date', false);
            gridColumnApi.setColumnVisible('exact_date', false);
            gridColumnApi.setColumnVisible('quote.last', false);
            gridColumnApi.setColumnVisible('quote.changePercent', false);
            gridColumnApi.applyColumnState({
              defaultState: { sort: null },
              state: [{ colId: 'historical_date', sort: 'desc' }],
            });
            break;
          case 1:
          case 3:
            gridColumnApi.setColumnVisible('historical_date', false);
            gridColumnApi.setColumnVisible('estimated_date', false);
            gridColumnApi.setColumnVisible('exact_date', true);
            gridColumnApi.setColumnVisible('quote.last', true);
            gridColumnApi.setColumnVisible('quote.changePercent', true);
            gridColumnApi.applyColumnState({
              defaultState: { sort: null },
              state: [{ colId: 'exact_date', sort: 'asc' }],
            });
            break;
          case 2:
            gridColumnApi.setColumnVisible('historical_date', false);
            gridColumnApi.setColumnVisible('estimated_date', false);
            gridColumnApi.setColumnVisible('exact_date', true);
            gridColumnApi.setColumnVisible('quote.last', false);
            gridColumnApi.setColumnVisible('quote.changePercent', false);
            gridColumnApi.applyColumnState({
              defaultState: { sort: null },
              state: [{ colId: 'exact_date', sort: 'asc' }],
            });
            break;
          default:
            break;
        }
        gridApi.sizeColumnsToFit();
      };

      toggleCatalystColumn(activeTabIndex);
    }
  }, [activeTabIndex, gridApi, gridColumnApi]);

  const onTableReady = (params: any) => {
    if (initialData) {
      setTableData(getTabDataByTabIndex(activeTabIndex, dataTabs));
    } else {
      fetchData(filters, activeTabKey);
    }
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);

    if (params?.api) {
      params.api.addGlobalListener((type: string) => {
        if (type === 'firstDataRendered') {
          if (window.outerWidth > 700) {
            initSearchTooltipHint();
            initEstimatedTabHint();
          }
        }
      });
    }

    setTableReady(true);
  };

  function initSearchTooltipHint() {
    initTableHint('.bz3-search-field', 'Search your biotech stocks!');
  }

  function initEstimatedTabHint() {
    const tooltipInstances = initTableHint(
      '#tab-estimated-dates',
      'You are currently looking at catalysts which have exact dates from the company. Click here to see binary events expected this month!',
    );

    const tabs = document.querySelector('.top-tabs');

    if (tabs) {
      tabs.addEventListener(
        'mouseover',
        () => {
          hideAll();

          if (Array.isArray(tooltipInstances)) {
            tooltipInstances.forEach(instance => instance.destroy());
          }
        },
        { once: true },
      );
    }
  }

  async function loadQuotesForData(data: any) {
    const dataTickers: string[] = [];

    if (Array.isArray) {
      data.forEach((item: any) => {
        if (Array.isArray(item.companies) && item.companies.length) {
          const ticker = item.companies[0].securities?.[0]?.symbol;

          if (ticker) {
            item.ticker = item.companies[0].securities?.[0]?.symbol;
            dataTickers.push(ticker);
          }
        }
      });
    }

    return injectQuotes(data, dataTickers);
  }

  const getTabDataCount = useCallback(
    (index: number) => {
      if (isLoading) return '-';

      const tabData = getTabDataByTabIndex(index, dataTabs);

      if (index === activeTabIndex) {
        if (currentFilteredRowsCount) {
          return currentFilteredRowsCount;
        }
      }

      return tabData?.length;
    },
    [activeTabIndex, currentFilteredRowsCount, dataTabs, isLoading],
  );

  function tabs(stat = true): any {
    const isHistoricalTab = activeTabIndex === 0;
    return [
      {
        key: FDACalendarTabKeys[0],
        title: isHistoricalTab && stat ? `Historical (${getTabDataCount(0)})` : `Historical`,
        tooltip: `Tab shows events that have happened in the past or which have no catalyst date`,
      },
      // {
      //   key: 'today',
      //   title: stat ? `Today (${getTabDataCount(1)})` : `Today`,
      //   tooltip: `Tab shows events on today`,
      // },
      {
        key: FDACalendarTabKeys[1],
        title: stat && !isHistoricalTab ? `Exact Dates (${getTabDataCount(1)})` : `Exact Dates`,
        tooltip: `Tab shows events with exact dates in the future`,
      },
      {
        key: FDACalendarTabKeys[2],
        title: stat && !isHistoricalTab ? `Estimated Dates (${getTabDataCount(2)})` : `Estimated Dates`,
        tooltip: `Tab shows future events expressed as a timeframe rather than an exact date`,
      },
      {
        key: FDACalendarTabKeys[3],
        title: stat && !isHistoricalTab ? `PDUFA (${getTabDataCount(3)})` : `PDUFA`,
        tooltip: ``,
      },
    ];
  }

  async function onTabChange(index: number) {
    if (activeTabIndex === index) return;

    const newTabKey = FDACalendarTabKeys[index];

    setActiveTabIndex(index);

    if (activeTabKey === 'historical') {
      await fetchData(filters, newTabKey);
    } else if (newTabKey === 'historical') {
      await fetchData(filters, newTabKey);
    }

    const data = getTabDataByTabIndex(index, dataTabs);
    if (data && data.length) {
      setTableData(data);
    }

    if (props.onTabChange) {
      const tabKey = tabs()?.[index]?.key;

      if (tabKey) {
        props.onTabChange(tabKey);
      }
    }

    if (!gridApi?.isAnyFilterPresent()) {
      setCurrentFilteredRowsCount(null);
    } else {
      updateFilteredRowsCount(gridApi);
    }
  }

  function setTableData(data: any) {
    setRowData(data);
  }

  const onFilterChanged = (params: any) => {
    setFilters(params);
    fetchData(params, activeTabKey);
  };

  function getTabIndexByKey(key?: string | number): number {
    if (key) {
      return FDACalendarTabKeys.findIndex(tabKey => {
        return tabKey === key;
      });
    } else {
      return 0;
    }
  }

  const handleBinaryOnly = (event: any) => {
    const value = event?.target?.checked;

    if (binaryEventsOnly !== value) {
      setBinaryEventsOnly(value);
      updateQueryParams({ binaryOnly: value }, 'replace');
    }
  };

  const BinaryEventsButton = () => {
    return (
      <BinaryEventsButtonWrapper>
        <Checkbox
          checked={binaryEventsOnly}
          id="binary-events-only"
          label="Binary Events Only"
          onChange={event => handleBinaryOnly(event)}
          style={{ fontWeight: 600 }}
        />
      </BinaryEventsButtonWrapper>
    );
  };

  const updateFilteredRowsCount = (api: any) => {
    if (!api) return;

    const counts = api?.isAnyFilterPresent() ? api.getDisplayedRowCount() : null;

    setCurrentFilteredRowsCount(counts);
  };

  const onGridFilterChanged = ({ api }: any) => {
    updateFilteredRowsCount(api);
  };

  const onRowDataChanged = ({ api }: any) => {
    updateFilteredRowsCount(api);
  };

  return (
    <Container>
      <Layout
        calendar={calendar}
        calendarData={calendarData}
        columnDefs={ColumnsDefinition}
        disableSettingGridApi={true}
        enableDatePicker={true}
        enableSearch
        filterBarChildren={<BinaryEventsButton />}
        hasStockTableStyling={hasStockTableStyling}
        height={height}
        hiddenColumns={hiddenColumns}
        initialDateRange={dateRange}
        initialTabIndex={activeTabIndex}
        onFilterChanged={onFilterChanged}
        onGridFilterChanged={onGridFilterChanged}
        onRowDataChanged={onRowDataChanged}
        onTabChange={onTabChange}
        onTableReady={onTableReady}
        rowHeight={190}
        searchKey="securities"
        tableData={rowData}
        tableLoading={isLoading}
        tabs={tabs()}
        ticker={queryParams?.symbol ?? initialTicker}
      />
    </Container>
  );
};

const Container = styled.div`
  .table-cell-outcome {
    white-space: normal;
  }
  .table-cell-get_alert {
    width: 70px;
  }
`;

const BinaryEventsButtonWrapper = styled('div')`
  margin-left: 30px;

  @media screen and (max-width: 1000px) {
    margin-bottom: 10px;
    margin-left: 0;
    margin-top: 0;
  }
`;
